import {EventEmitter} from "eventemitter3";

import {Vec2} from "../core/Vec2.js";
import {Player} from "../player/Player.js";
import {GameServer} from "./GameServer.js";
import {SimulationEvent} from "./SimulationManager.js";

/**
 * NEW: The shape of augmented event data provided by the GameManager.
 */
export interface AugmentedEvent extends SimulationEvent {
  isPrimary: boolean;
  serverId: string;
}

/**
 * NEW: The shape of the player data provided for rendering.
 */
export interface RenderablePlayer {
  player: Player;
  subStepAlpha: number;
  isPrimary: boolean;
  serverId: string;
  smoothPlayerPosition: Vec2; // UPDATED: Added the smoothed position
}

// Update the event map for the GameManager's EventEmitter
type GameManagerEvents = {
  "round-started": (server: GameServer, isPrimary: boolean) => void;
  "simulation-event": (event: AugmentedEvent) => void;
  "round-finished": (
    result: {completed: boolean; dead: boolean},
    isPrimary: boolean,
    serverId: string,
  ) => void;
};

/**
 * A private interface to hold the rendering state for a player.
 */
interface PlayerRenderState {
  smoothPosition: Vec2;
}

export class GameManager extends EventEmitter<GameManagerEvents> {
  private servers = new Map<string, GameServer>();
  private primaryServerId: string | null = null;

  // NEW: A map to store the smoothed rendering state for each player.
  // The key is a unique identifier like "serverId:playerId".
  private playerRenderState = new Map<string, PlayerRenderState>();

  // NEW: A factor controlling how quickly the rendered position catches up to the
  // simulation's position. A smaller value means smoother but more delayed movement.
  // A value of 1 would disable smoothing entirely.
  private readonly SMOOTHING_FACTOR = 0.1;

  /**
   * Designates one of the managed servers as the "primary" one.
   * @param serverId The unique ID of the GameServer to set as primary.
   */
  public setPrimaryServer(serverId: string | null): void {
    if (serverId && !this.servers.has(serverId)) {
      console.warn(
        `Attempted to set non-existent server "${serverId}" as primary.`,
      );
      return;
    }
    this.primaryServerId = serverId;
  }

  public add(server: GameServer): () => void {
    if (this.servers.has(server.id)) return () => null;

    this.servers.set(server.id, server);

    const onRoundStarted = (srv: GameServer) => {
      this.emit("round-started", srv, srv.id === this.primaryServerId);
    };
    const onSimEvent = (event: any) => {
      this.emit("simulation-event", {
        ...event,
        isPrimary: server.id === this.primaryServerId,
        serverId: server.id,
      });
    };
    const onFinish = (result: any) => {
      this.emit(
        "round-finished",
        result,
        server.id === this.primaryServerId,
        server.id,
      );
    };

    server.on("round-started", onRoundStarted);
    server.on("simulation-event", onSimEvent);
    server.on("round-finished", onFinish);

    return () => {
      server.off("round-started", onRoundStarted);
      server.off("simulation-event", onSimEvent);
      server.off("round-finished", onFinish);

      // NEW: Clean up render state for players on the removed server.
      for (const player of server.simulation.players.values()) {
        const renderKey = `${server.id}:${player.id}`;
        this.playerRenderState.delete(renderKey);
      }

      server.destroy();
      this.servers.delete(server.id);
    };
  }

  public update(serverNow: number): void {
    for (const server of this.servers.values()) {
      server.update(serverNow);
    }
  }

  /**
   * Gets all players from all managed servers, augmented with rendering data.
   * This now includes a smoothed position that corrects for simulation rollbacks.
   */
  public getAllPlayers(): RenderablePlayer[] {
    const renderablePlayers: RenderablePlayer[] = [];
    // NEW: Keep track of players we see this frame to clean up old ones.
    const activePlayerKeys = new Set<string>();

    for (const server of this.servers.values()) {
      const isPrimary = server.id === this.primaryServerId;

      for (const player of server.simulation.players.values()) {
        const renderKey = `${server.id}:${player.id}`;
        activePlayerKeys.add(renderKey);

        // This is the "correct" position according to the simulation at this exact moment.
        // It can jump around due to rollbacks.
        let authoritativePosition = player.previousPosition
          .clone()
          .lerp(player.currentPosition, server.subStepAlpha);
        if (player.isCompleted || player.isDead)
          authoritativePosition = player.currentPosition.clone();

        let renderState = this.playerRenderState.get(renderKey);

        if (!renderState) {
          // First time we see this player. Initialize their render state.
          // The smooth position starts at the authoritative position.
          renderState = {
            smoothPosition: authoritativePosition.clone(),
          };
          this.playerRenderState.set(renderKey, renderState);
        } else {
          // This player already has a render state. Smoothly move their
          // last known smooth position towards the new authoritative position.
          // This lerp dampens any sudden jumps from the simulation.
          if (!renderState.smoothPosition.equals(authoritativePosition)) {
            renderState.smoothPosition.lerp(
              authoritativePosition,
              this.SMOOTHING_FACTOR,
            );
          }
        }

        renderablePlayers.push({
          player: player,
          subStepAlpha: server.subStepAlpha,
          isPrimary: isPrimary,
          serverId: server.id,
          // Provide a clone to prevent external modification of our internal state.
          smoothPlayerPosition: renderState.smoothPosition.clone(),
        });
      }
    }

    // NEW: Clean up state for players that are no longer in any simulation.
    for (const key of this.playerRenderState.keys()) {
      if (!activePlayerKeys.has(key)) {
        this.playerRenderState.delete(key);
      }
    }

    return renderablePlayers;
  }
}
